import React, { useEffect, useRef, useState } from 'react';

import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';

import { getLogger } from 'app-components/utils/core';

import { ROOT_MODAL_DRAWER_PROP } from 'components/Drawers/standardDrawerContract';
import type { RootModalDrawerOptions } from 'components/Drawers/standardDrawerContract';

import { getModalProps } from './drawerPresentation';
import { getLoadedModalComponent, MODAL_COMPONENTS, toModalComponent } from './modalRegistry';
import type { AsyncModalModule, ModalComponent, ModalProps, RegistryComponent, RootModalRecord } from './types';
import { useRemoveRootModalRecord } from './useRootModalUrlDialogs';

const log = getLogger('components/RootModal/ModalRenderer');

type AsyncModalWrapperProps = {
  component: Promise<AsyncModalModule>;
  dialogType: string;
  modal: RootModalRecord;
  modalProps: ModalProps;
  onLoadSettled?: () => void;
};

type ModalRendererProps = {
  dialogType: string;
  modal: RootModalRecord;
  // Called when a lazily registered modal's code loads or fails to load.
  onLoadSettled?: () => void;
  onRootModalDrawerClose?: () => void;
  onRootModalDrawerDirtyChange?: (isDirty: boolean) => void;
  rootModalDrawer?: RootModalDrawerOptions;
};

function AsyncModalLoadingFallback({ rootModalDrawer }: { rootModalDrawer?: RootModalDrawerOptions }) {
  if (!rootModalDrawer) {
    return null;
  }

  return (
    <Box
      aria-label='Loading dialog'
      data-testid='root-modal-drawer-loading'
      role='status'
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        height: '100%',
        p: 3,
      }}
    >
      <Skeleton variant='text' width='55%' height={40} />
      <Skeleton variant='rounded' width='100%' height={72} />
      <Skeleton variant='rounded' width='100%' height={120} />
      <Skeleton variant='rounded' width='100%' height={180} />
    </Box>
  );
}

// A modal (unlike a drawer) has no shell to show a load error in. Close its record instead, so it can't pop up
// uninvited on a later re-render and the next open retries the load.
function CloseFailedModal({ dialogId }: { dialogId: string }): null {
  const removeRootModalRecord = useRemoveRootModalRecord();
  useEffect(() => {
    removeRootModalRecord(dialogId);
  }, [dialogId, removeRootModalRecord]);
  return null;
}

const AsyncModalWrapper = React.memo(function AsyncModalWrapper({
  component,
  dialogType,
  modal,
  modalProps,
  onLoadSettled,
}: AsyncModalWrapperProps) {
  // Render an already-loaded modal in the first commit, as an eagerly registered one would.
  const [ModalComponent, setModalComponent] = useState<ModalComponent | null>(
    () => getLoadedModalComponent(dialogType) ?? null,
  );
  const [loadFailed, setLoadFailed] = useState(false);
  // The getter hands a new promise on every render, so report a load to RootModal only the first time it lands.
  const reportedLoadRef = useRef(ModalComponent !== null);
  useEffect(() => {
    let cancelled = false;
    setLoadFailed(false);
    component
      .then((resolvedComponent) => {
        if (!cancelled) {
          const ResolvedModalComponent = toModalComponent(resolvedComponent);
          setModalComponent(() => ResolvedModalComponent);
          if (!reportedLoadRef.current) {
            reportedLoadRef.current = true;
            onLoadSettled?.();
          }
        }
      })
      .catch((error) => {
        log.error('Failed to load modal component:', error);
        if (!cancelled) {
          setLoadFailed(true);
          onLoadSettled?.();
        }
      });

    return () => {
      cancelled = true;
    };
  }, [component, onLoadSettled]);

  if (loadFailed) {
    if (!modalProps[ROOT_MODAL_DRAWER_PROP]) {
      return <CloseFailedModal dialogId={modal.id} />;
    }
    return (
      <Box aria-label='Unable to load dialog' role='alert' sx={{ p: 3 }}>
        <Typography variant='titleMedium'>Unable to load this drawer</Typography>
        <Typography color='text.secondary' sx={{ mt: 1 }} variant='bodyRegular'>
          Close it and try again.
        </Typography>
      </Box>
    );
  }

  if (!ModalComponent) {
    return <AsyncModalLoadingFallback rootModalDrawer={modalProps[ROOT_MODAL_DRAWER_PROP] as RootModalDrawerOptions} />;
  }

  return <ModalComponent type={modal.type} dialogId={modal.id} {...modalProps} />;
});

function ModalRenderer({
  modal,
  dialogType,
  onLoadSettled,
  onRootModalDrawerClose,
  onRootModalDrawerDirtyChange,
  rootModalDrawer,
}: ModalRendererProps) {
  const component = MODAL_COMPONENTS[dialogType] as RegistryComponent | undefined;

  if (!modal || !component) {
    return null;
  }

  const modalProps = getModalProps(modal);
  if (rootModalDrawer) {
    modalProps.onRootModalDrawerClose = onRootModalDrawerClose;
    modalProps.onRootModalDrawerDirtyChange = onRootModalDrawerDirtyChange;
    modalProps[ROOT_MODAL_DRAWER_PROP] = rootModalDrawer;
  }

  // An ImportModalComponent getter returns a promise and starts loading the
  // dialog's code.
  if (component instanceof Promise) {
    return (
      <AsyncModalWrapper
        component={component}
        dialogType={dialogType}
        modal={modal}
        modalProps={modalProps}
        onLoadSettled={onLoadSettled}
      />
    );
  }

  const ModalComponent = component;
  return <ModalComponent type={modal.type} dialogId={modal.id} {...modalProps} />;
}

export default React.memo(ModalRenderer);
